import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FormEditPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSubmission();
  }, [submissionId]);

  const fetchSubmission = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const subRes = await axios.get(`${API}/forms/submissions/${submissionId}`, { headers });
      setSubmission(subRes.data);
      setFormData(subRes.data.data || {});

      // Fetch template for field definitions
      if (subRes.data.template_id) {
        const templateRes = await axios.get(`${API}/forms/templates/${subRes.data.template_id}`, { headers });
        setTemplate(templateRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      toast.error('Failed to load submission');
      navigate('/dashboard/forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckboxChange = (fieldId, optionValue, checked) => {
    setFormData(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, optionValue] };
      } else {
        return { ...prev, [fieldId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`${API}/forms/submissions/${submissionId}`, {
        data: formData
      }, { headers });

      toast.success('Submission updated successfully');
      navigate(`/dashboard/forms/view/${submissionId}`);
    } catch (err) {
      console.error('Save failed:', err);
      if (err.response?.status === 403) {
        toast.error('Only admins can edit submissions');
      } else {
        toast.error('Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.id];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <Input
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <Textarea
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              rows={4}
            />
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <Input
              type="date"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <Input
              type="time"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select an option...</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'multiselect':
      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {field.options?.map((option) => (
                <label key={option.value} className="flex items-center gap-2 p-2 rounded border bg-background hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(value || []).includes(option.value)}
                    onChange={(e) => handleCheckboxChange(field.id, option.value, e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label}</Label>
            <Input
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!submission || !template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Submission not found</p>
        <Link to="/dashboard/forms">
          <Button variant="outline" className="mt-4">Back to Forms</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="form-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/dashboard/forms/view/${submissionId}`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Edit Submission</h1>
            <p className="text-sm text-muted-foreground">{submission.template_name}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Edit Notice */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-200 font-medium">Admin Edit Mode</p>
          <p className="text-xs text-amber-300/70">
            You are editing a submitted form. Changes will be logged with your name and timestamp.
            Originally submitted by {submission.submitted_by} on {new Date(submission.submitted_at).toLocaleString()}.
          </p>
        </div>
      </div>

      {/* Form Sections */}
      {template.sections?.map((section) => (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle>{section.title}</CardTitle>
            {section.description && (
              <p className="text-sm text-muted-foreground">{section.description}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {section.fields?.map((field) => renderField(field))}
          </CardContent>
        </Card>
      ))}

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate(`/dashboard/forms/view/${submissionId}`)}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
